module Client.Login

open Elmish
open Fable.Remoting.Client
open Shared
open Shared.LogIn
open Illy.SafeStack
open Illy.SafeStack.Client
open Illy.SafeStack.Validation
open Fable.Core.JsInterop

module ResetPassword =
    open Shared.PasswordReset.ResetEmail
    open Fable.React
    open Fable.React.Props

    type Model =
        {
            EmailAddress: Text.Model
            PasswordReset: bool
            Resetting: bool
            ApiError: bool
        }

    type Msg =
        | ResetClicked
        | CloseOverlay
        | EmailAddressMsg of Text.Msg
        | ApiError of ApiError
        | ResetResponse of FailureReason option

    type Event =
        | CloseOverlay

    module Validations =
        open Illy.SafeStack.CompositionalValidation

        let resetValidations (m: Model) =
            {m with
                EmailAddress = Text.resetValidations m.EmailAddress
            }

        let dispatchValidations model = 
            let model = resetValidations model 
            function 
            | ValidationResult.Passed -> model 
            | ValidationResult.Warned _ -> failwith "Did not expect warnings"
            | ValidationResult.Failed (err, _) ->
                let errors = err |> List.map Validations.Email.EmailValidations.toString |> concatOptional
                { model with EmailAddress = Text.setValidations model.EmailAddress errors None }

    let init() =
        {
            EmailAddress =
                Text.init 
                    "resetemail" 
                    "email address" 
                    None 
                    250.
                    true
                    None
                    (Text.CustomSettings.defaultOptions())
            PasswordReset = false
            Resetting = false
            ApiError = false
        }, Cmd.none

    let update msg model =
        match msg with
        | Msg.CloseOverlay -> model, SideEffect.Event CloseOverlay
        | EmailAddressMsg eMsg ->
            let m, c = Text.update eMsg model.EmailAddress
            {model with EmailAddress = m}, Cmd.map EmailAddressMsg c |> SideEffect.Cmd
        | ResetClicked ->
            let emailStr = Text.toString model.EmailAddress
            {model with 
                Resetting = true
            },
            Api.unsecureCall<ILoginApi,_,_,_> (fun api -> api.resetPassword) emailStr ResetResponse Msg.ApiError
            |> SideEffect.Cmd
        | Msg.ApiError _ ->
            //this overlay has its own custom error handling
            {model with ApiError = true}, SideEffect.none
        | ResetResponse ret ->
            match ret with
            | None -> {model with PasswordReset = true; Resetting = false}, SideEffect.none
            | Some (FailedValidations v) ->
                Validations.dispatchValidations {model with Resetting = false} v, SideEffect.none
            | Some EmailSendingError ->
                { model with
                    EmailAddress = Text.setValidations model.EmailAddress (Some "We are having trouble sending emails at the moment.") None
                    Resetting = false
                }, SideEffect.none
            | Some (Throttle d) ->
                let msg =
                    if d <= 120 then
                        sprintf "The server is busy. Please wait %d seconds and try again" d
                    else
                        "The server is busy. Please try again later."
                { model with
                    EmailAddress = Text.setValidations model.EmailAddress (Some msg) None
                    Resetting = false
                }, SideEffect.none

    let renderResetSuccess =
        p [] [
            FontAwesome.solid "check-circle" [ FontAwesome.Fixed ] []
            b [] [ str "An email containing reset information has been sent to the requested email address." ]
            br []
            str "Please note that the email must be actioned within a limited time."
        ]

    let renderResetApiError =
        p [ Class "ui-state-error" ] [
            FontAwesome.solid "exclamation-triangle" [ FontAwesome.Fixed ] []
            str "Sorry, your request could not be handled at this time."
        ]

    let renderEditContent model dispatch =
        [
            p [ Id "reset_standard" ] [
                str "To reset your password, enter the email address registered against your account. Instructions will be sent there to complete the process."
            ]
            div [] [
                jQueryUI.inputGroupWithClasses (if model.EmailAddress.Control.Error.IsSome then ["ui-state-error"] else []) [
                    div [ ClassName "label" ] [
                        let control = model.EmailAddress.Control
                        jQueryUI.labelWithMandatory control.Id control.Label control.Error control.Warning false control.Mandatory
                    ]
                    Text.textInput false model.EmailAddress (EmailAddressMsg >> dispatch)
                    str " "
                    jQueryUI.buttonWithDisabled
                        model.Resetting
                        (Some "ui-state-default")
                        "Send reset instructions"
                        ResetClicked
                        dispatch
                ]                
            ]
        ]

    let view model dispatch = 
        jQueryUI.modal "Forgot your password?" 1000 Msg.CloseOverlay dispatch (Some 500) [
            if model.PasswordReset then yield renderResetSuccess
            else if model.ApiError then yield renderResetApiError
            else yield! renderEditContent model dispatch
        ]

type LoginErrorType =
    | Message of string
    | Timeout of int

type Model =
    {
        Username: Text.Model
        Password: Text.Model
        LoggingIn: bool
        LoginError: LoginErrorType option
        EnvironmentType: EnvironmentType
        EnvironmentName: string
        SupportMessage: string option
        ResetPassword: ResetPassword.Model option
        IGMessage: string option
        TimedOut: bool
        ADAccounts: string list
        WindowsAuthEndpoint: string option
        OpenIdEndpoint: string option
        Loading: bool
    }

type Msg =
    | SetUsername of Text.Msg
    | PasswordMsg of Text.Msg
    | LoginClicked
    | LoginResponse of LoginResult
    | LoadedContext of LoginContext
    | ResetPasswordMsg of ResetPassword.Msg
    | OpenResetPasswordOverlay
    | LoggedIn
    | MovingToMFA
    | StorageError of exn
    | TickCounter
    | ADLogin of string
    | AzureLogin
    | AzureClicked
    | AzureSuccess
    | WindowsAccountsLoaded of string list
    | WindowsAuthTokenReceived of string

[<Literal>]
let AD_REDIRECT_COOKIE = "ad-redirect"
let AD_REDEEM_TOKEN_COOKIE = "ad-token"

module Calls =
    open Fetch

    let login (model: Model) =
        Api.unsecureCallSuccess<ILoginApi,_,_,_>
            (fun api -> api.login)
            (Entered {
                Username = Text.toString model.Username |> Option.defaultValue ""
                Password = Text.toString model.Password |> Option.defaultValue ""
            })
            LoginResponse

    let adLogin code =
        Api.unsecureCallSuccess<ILoginApi,_,_,_>
            (fun api -> api.login)
            (AD code)
            LoginResponse

    let getContext() =
        Api.unsecureCallSuccess<ILoginApi,_,_,_>
            (fun api -> api.loadContext)
            ()
            LoadedContext

    let ad url account =
        let query =
            promise {
                let props =
                    [
                        RequestProperties.Credentials RequestCredentials.Include
                        RequestProperties.Method HttpMethod.POST
                    ]
                let combinedUrl = url + "/wa?account=" + account
                let! response = GlobalFetch.fetch(RequestInfo.Url combinedUrl, requestProps props)
                return! response.text()
            }
        Cmd.OfPromise.perform
            (fun _ -> query)
            ()
            WindowsAuthTokenReceived

    let loadAccounts url =
        let query =
            promise {
                let props =
                    [
                        RequestProperties.Credentials RequestCredentials.Include
                        RequestProperties.Method HttpMethod.GET
                    ]
                let! response = GlobalFetch.fetch(RequestInfo.Url (url + "/list"), requestProps props)
                return! response.json<string list>()
            }
        Cmd.OfPromise.perform
            (fun _ -> query)
            ()
            WindowsAccountsLoaded

    let redeemOIDToken (token: System.Guid) =
        Api.unsecureCallSuccess<ILoginApi,_,_,_>
            (fun api -> api.openIdLogin)
            token
            LoginResponse

    let azure endpoint =
        let query url =
            promise {
                let props =
                    [
                        RequestProperties.Credentials RequestCredentials.Include
                        RequestProperties.Method HttpMethod.POST
                        RequestProperties.Redirect RedirectMode.Manual
                    ]
                let! response = GlobalFetch.fetch(RequestInfo.Url url, requestProps props)
                let! _ = response.text()
                match SafeStack.Client.Cookies.tryFind AD_REDIRECT_COOKIE with
                | Some red ->
                    let url = red.Replace("***", "&")
                    printfn "rd url: %s" red
                    Browser.Dom.window.location.href <- url
                | _ -> failwith "No redirect correlation cookie found"
            }
        Cmd.OfPromise.perform
            (fun _ -> query endpoint)
            ()
            (fun _ -> AzureSuccess)

module Validations =
    open Shared.LogIn.Validations

    let resetLoginValidations (m: Model) =
        {m with
            Username = Text.resetValidations m.Username
            Password = Text.resetValidations m.Password
            LoginError = None
        }

    let dispatchLoginValidations model = 
        let model = resetLoginValidations model 

        function 
        | ValidationResult.Passed -> model 
        | ValidationResult.Warned _ -> failwith "Did not expect warnings"
        | ValidationResult.Failed (err, _) ->
            let errors = 
                err 
                |> ValidationInfrastructure.bundleValidations 
                    (fun x -> 
                        (match x with 
                        | LogInValidations.Username _ -> 0 
                        | LogInValidations.Password _ -> 1
                        | LogInValidations.GeneralError _ -> 2
                        | LogInValidations.Timeout _ -> 3), LogInValidations.toString x)
                    4
            { 
                model with 
                    Username = Text.setValidations model.Username errors.[0] None
                    Password = Text.setValidations model.Password errors.[1] None
                    LoginError = 
                        match errors.[2], errors.[3] with
                        | None, None -> None
                        | Some err, _ -> err |> Message |> Some
                        | None, Some timeout -> System.Int32.Parse timeout |> LoginErrorType.Timeout |> Some
            }

let init() =
    let oidToken = SafeStack.Client.Cookies.tryFind AD_REDEEM_TOKEN_COOKIE
    SafeStack.Client.Cookies.clear()

    {
        Username = Text.init "username" "username" (Some "Enter your username") 250. true None (Text.CustomSettings.defaultOptions())
        Password = Text.init "password" "password" (Some "Enter your password") 250. true None ({ Text.CustomSettings.defaultOptions() with TextType = Text.TextType.Password})
        LoggingIn = false
        LoginError = None
        EnvironmentType = EnvironmentType.Dev
        EnvironmentName = "Unable to load Environment"
        SupportMessage = None
        ResetPassword = None
        TimedOut = false
        IGMessage = None
        ADAccounts = []
        OpenIdEndpoint = None
        WindowsAuthEndpoint = None
        Loading = true
    },
    Cmd.batch [
        yield Calls.getContext()

        match oidToken with
        | None -> ()
        | Some token -> yield Calls.redeemOIDToken (System.Guid.Parse token) 
    ]

let dispatchResetPasswordlMsg model msg =
    match model.ResetPassword with
    | None -> model, Cmd.none
    | Some re ->
        let m, eff = ResetPassword.update msg re
        match eff with
        | SideEffect.Cmd c -> {model with ResetPassword = Some m}, Cmd.map ResetPasswordMsg c
        | SideEffect.Event ev ->
            match ev with
            | ResetPassword.Event.CloseOverlay ->
                {model with ResetPassword = None}, Cmd.none

let onUserLoggedIn (user: ClientSideUser) model =
    SafeStack.Client.Cookies.remove AD_REDIRECT_COOKIE

    let cmd =
        Cmd.OfAsync.either
            (Storage.store Storage.USER_STORAGE_KEY) 
            user 
            (fun _ -> LoggedIn)
            StorageError

    { model with LoggingIn = false }, cmd

let update (msg: Msg) (model: Model)=
    match msg with
    | OpenResetPasswordOverlay ->
        let m, c = ResetPassword.init()
        { model with ResetPassword = Some m}, Cmd.map ResetPasswordMsg c
    | ResetPasswordMsg pMsg -> dispatchResetPasswordlMsg model pMsg
    | TickCounter ->
        match model.LoginError with
        | Some (LoginErrorType.Timeout t) ->
            if t <= 1 then
                {
                    model with
                        LoginError = None
                        TimedOut = false
                }, Cmd.none
            else
                {
                    model with
                        LoginError = LoginErrorType.Timeout (t-1) |> Some
                }, setTimeout 1000 () (fun _ -> TickCounter) StorageError
        | _ -> model, Cmd.none
    | LoadedContext c ->
        {
            model with
                EnvironmentType = c.EnvType
                EnvironmentName = c.EnvName
                SupportMessage = c.SupportMessage
                IGMessage = c.IGMessage
                WindowsAuthEndpoint = c.WindowsAuth
                OpenIdEndpoint =
                    if c.SupportsOIDC then
                        let prefix =
                            match c.VirtualPath with
                            | None -> ""
                            | Some p -> "/" + p
                        prefix + "/api/openid/login" |> Some
                    else
                        None
                Loading = false
        },
        match c.WindowsAuth with
        | None -> Cmd.none
        | Some url -> Calls.loadAccounts url
    | WindowsAccountsLoaded accounts -> { model with ADAccounts = accounts }, Cmd.none
    | SetUsername umsg -> 
        let tmod, tcmd = Text.update umsg model.Username
        { model with 
            Username = tmod 
            LoggingIn = false
        }, Cmd.map SetUsername tcmd
    | PasswordMsg pMsg ->
        let m, c = Text.update pMsg model.Password
        { model with 
            Password = m 
            LoggingIn = false
        }, Cmd.map PasswordMsg c
    | LoginClicked ->
        let vResult =
            Validations.validateCredentials
                (Text.toString model.Username)
                (Text.toString model.Password)
        if Validation.isSuccessful false vResult then 
            {model with LoggingIn = true},
            Calls.login model
        else
            Validations.dispatchLoginValidations model vResult, Cmd.none
    | ADLogin acct ->
        match model.WindowsAuthEndpoint with
        | None ->
            { model with 
                LoggingIn = false
                LoginError = Some <| LoginErrorType.Message "Your configuration is incorrect for Windows Auth." 
            }, Cmd.none
        | Some url ->
            { model with LoggingIn = true }, Calls.ad url acct
    | AzureSuccess ->
        { model with LoggingIn = false; }, Cmd.none
    | AzureClicked -> model, Cmd.none
    | AzureLogin ->
        { model with LoggingIn = true }, Calls.azure (model.OpenIdEndpoint |> Option.defaultWith (fun _ -> failwith "Calling AzureLogin with no endpoint"))
    | WindowsAuthTokenReceived token ->
        printfn "Got this token: %s" token
        model, Calls.adLogin token
    | LoginResponse response ->
        match response with
        | Successful user -> onUserLoggedIn user model   
        | PasswordExpired ->
            { model with 
                LoggingIn = false
                LoginError = Some <| LoginErrorType.Message "Your password has expired. An email has been sent to the registered email address containing a link to update the password." 
            }, Cmd.none
        | LoginFailure err ->
            let model' = Validations.dispatchLoginValidations {model with LoggingIn = false} err
            let timeout, cmd =
                match model'.LoginError with
                | Some (LoginErrorType.Timeout t) -> 
                    true, setTimeout 1000 () (fun _ -> TickCounter) StorageError
                | _ -> 
                    false, Cmd.none

            {
                model' with
                    TimedOut = timeout
            }, cmd
        | MFARequired(mode, user) ->
            let cmd =
                Cmd.OfAsync.either
                    (Storage.store Storage.USER_STORAGE_KEY) 
                    user 
                    (fun _ -> MovingToMFA)
                    StorageError
            model, cmd
    | MovingToMFA ->
        model, Routing.PageRoute.EnterMFA |> Routing.PageRoute.toPath |> Elmish.Navigation.Navigation.newUrl
    | StorageError exn ->
#if DEBUG
        printfn "There was a problem saving your login to the browser: %O" exn
#endif
        model, Cmd.none  
    | LoggedIn ->
        model, Routing.PageRoute.Home |> Routing.PageRoute.toPath |> Elmish.Navigation.Navigation.newUrl

open Fable.React
open Fable.React.Props
open Fable.Core.JsInterop

let toEnvName =
    function
    | LIVE -> "live"
    | UAT -> "uat"
    | Dev -> "uat"
    | _ -> ""

let renderFooter (model: Model) =
    footer [] [
        ul [] [
            match model.SupportMessage with
            | None -> ()
            | Some support ->
                yield li [] [ 
                    str "Support: " 
                    str support
                ]
            yield li [] [
                a [ Target "_blank"; Href "https://illy-servicedesk.atlassian.net/servicedesk/customer/portal/1" ] [ str "ILLY Online" ]
            ]
            yield li [] [
                a [ Target "_blank"; Href $"%s{Route.LINKS_MANAGER_PATH}/policies/Privacy policy - LINKS Carepath.pdf" ] [ str "Privacy Policy" ]
            ]
        ]
    ]

let renderAD model dispatch =
    [
        h1 [] [ str "Click account to log in with Active Directory" ]
        div [ ClassName "ad" ] [
            for acct in model.ADAccounts do
                yield Client.UI.button acct (Msg.ADLogin acct) dispatch
        ]
    ]

let view (model: Model) (dispatch: Msg -> unit) =
    div [ Id "login" ] [
        yield div [
            yield Id "wrap"
            if model.IGMessage.IsSome then yield Class "withIGMessage"
        ] [
            form [
                yield Id "form";
                match model.OpenIdEndpoint with None -> () | Some endp -> yield Action endp
            ] [
                div [ Id "profile-management" ] [
                    div [ ClassName ("rectangle " + toEnvName model.EnvironmentType) ] [
                        h2 [ Class (jQueryUI.addSkeletonScreen "" None model.Loading) ] [ str model.EnvironmentName ]
                    ]
                    div [ ClassName "triangle-l" ] []
                    div [ ClassName "triangle-r" ] []
                    section [] [
                        if model.ADAccounts <> [] then
                            yield! renderAD model dispatch
                            yield h3 [] [ str "or, log in to another account" ]
                        else
                            yield h1 [] [ str "log in to your account" ]
                    ]
                    div [ OnKeyPress (fun kbe -> if kbe.key = "Enter" then dispatch LoginClicked) ] [
                        yield Text.text false model.Username (SetUsername >> dispatch)
                        yield Text.text false model.Password (PasswordMsg >> dispatch)

                        match model.LoginError with
                        | None -> ()
                        | Some error ->
                            let msg =
                                match error with
                                | Message emsg -> emsg
                                | Timeout t ->  sprintf "You can try to log in again in %d seconds." t
                            
                            yield div [ 
                                Id "loginError"
                                ClassName "ui-state-error"
                            ] [
                                str msg
                            ]

                        match model.IGMessage with
                        | None -> ()
                        | Some igMsg ->
                            yield jQueryUI.inputGroup [
                                label [] [
                                    str "By logging in you agree to the following terms and conditions:"
                                ]
                                div [
                                    Id "loginIGMessage"
                                ] [
                                    str igMsg
                                ]
                            ]

                        if model.OpenIdEndpoint.IsSome && System.String.IsNullOrWhiteSpace(model.OpenIdEndpoint.Value) = false then
                            yield UI.button
                                "Login with Azure"
                                    AzureLogin
                                    dispatch

                        yield UI.buttonWithDisabled 
                            (model.LoggingIn || model.TimedOut)
                            "Log in"
                            LoginClicked
                            dispatch
                        yield a [ 
                            Id "passwwordReset"
                            OnClick (fun _ -> dispatch OpenResetPasswordOverlay)
                        ] [ str "forgot your password?" ]
                    ]
                ]
            ]
        ]

        yield renderFooter model
        yield! jQueryUI.conditionallyRenderView model.ResetPassword ResetPassword.view ResetPasswordMsg dispatch
    ]